import { Fragment, useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { useBlocker } from "@tanstack/react-router";
import { ApiHata } from "@/lib/api";
import { dosyaYukle, YuklemeHatasi } from "@/lib/veri";
import { Band, Buton, Cip, Etiket, Modal, girdi, tabloBas, tabloHucre } from "@/components/parcalar";
import { cevir } from "@/lib/dil";
import { cn } from "@/lib/cn";
import { secimeEkle } from "@/lib/mrb-toplu";
import {
  dosyalariEkle,
  etkinRn,
  excelsizDene,
  istekParcalari,
  rnSec,
  satirGorunumu,
  siradaki,
  sonucuIsle,
  tekrarDene,
  topluOzet,
  type Gonderim,
  type SokumSatiri,
  type TopluYaniti,
} from "@/lib/sokum-toplu";

/** Kalıptaki {0}, {1} … yerine düğüm koyar: sayılar kalın yazılır, Türkçede kelime sırası değişebilir. */
function yerlestir(kalip: string, degerler: ReactNode[]): ReactNode {
  return kalip.split(/(\{\d+\})/).map((p, i) => {
    const m = /^\{(\d+)\}$/.exec(p);
    return <Fragment key={i}>{m ? degerler[Number(m[1])] : p}</Fragment>;
  });
}

const METIN_RENGI = { "ink-2": "text-ink-2", "ink-3": "text-ink-3", warn: "text-warn", bad: "text-bad" } as const;

/** Bir satırı gönderir; sonucu ham olarak döner (satıra çevirmek `sonucuIsle`'nin işi). */
async function gonder(s: SokumSatiri): Promise<Gonderim> {
  const { dosya, dosyaAlan, alanlar, ekDosyalar } = istekParcalari(s);
  try {
    return { tur: "yanit", govde: await dosyaYukle<TopluYaniti>("/sokum/toplu", dosya, alanlar, ekDosyalar, dosyaAlan) };
  } catch (e) {
    if (e instanceof YuklemeHatasi) return { tur: "hata", status: e.status, govde: e.govde, mesaj: e.message };
    if (e instanceof ApiHata) return { tur: "hata", status: e.status, govde: null, mesaj: e.message };
    return { tur: "ag" };
  }
}

/**
 * D-061 "Upload Disassembly reports": birden çok söküm raporu PDF'i ve söküm girdi excel'i seçilir ya da sürüklenir; aynı riserin
 * PDF'i ve excel'i tek satırda eşlenir. Satırlar tek tek, sırayla `/api/sokum/toplu` ucuna gider; biri hata verse de ötekiler devam
 * eder. Düzen ve döngü MRB penceresinin (MrbTopluYukleme, D-060) eşidir: yükleme sürerken pencere kapanmaz ve uygulama içinde başka
 * sayfaya gidilmez; "Stop after this file" bekleyenleri göndermez. Satır listesi bu bileşenin durumunda durur: arkadaki liste
 * yenilense de bozulmaz. `rnler`: riseri dosya adından okunamayan satırda seçilebilecek riserler. Her tur bitince `onBitti`.
 */
export function SokumTopluYukleme({ rnler, onKapat, onBitti }: { rnler: string[]; onKapat: () => void; onBitti: () => void }) {
  const [satirlar, setSatirlar] = useState<SokumSatiri[]>([]);
  // Döngü en güncel listeyi okusun diye durumun bir kopyası ref'te; ikisi hep birlikte yazılır.
  const liste = useRef<SokumSatiri[]>([]);
  const yaz = useCallback((f: (l: SokumSatiri[]) => SokumSatiri[]) => {
    liste.current = f(liste.current);
    setSatirlar(liste.current);
  }, []);
  const [calisiyor, setCalisiyor] = useState(false);
  const calisiyorRef = useRef(false);
  /**
   * Döngünün göndereceği satırlar (`siradaki`): null = bütün bekleyenler ("Upload" turu); küme = yalnız bunlar. "Stop after this file"
   * ve tur sonu boş küme yapar; sonra satır düğmesi yalnız kendi satırını ekler.
   */
  const secim = useRef<ReadonlySet<number> | null>(null);
  /** Pencere kaldırıldı: döngü o anki dosyadan sonra durur. */
  const dur = useRef(false);
  const [durduruluyor, setDurduruluyor] = useState(false);
  /** İlk "Upload"tan sonra satırdaki "Retry" o satırı hemen gönderir; öncesinde yalnız sıraya koyar. */
  const [basladi, setBasladi] = useState(false);
  const [atlanan, setAtlanan] = useState<string[]>([]);
  const [rnTaslak, setRnTaslak] = useState<Record<number, string>>({});
  const [surukleniyor, setSurukleniyor] = useState(false);
  const secici = useRef<HTMLInputElement>(null);

  // Bırakma alanının dışına düşen dosyayı tarayıcı açmasın: sayfa değişir, yükleme listesi kaybolur.
  useEffect(() => {
    const engelle = (e: DragEvent) => e.preventDefault();
    window.addEventListener("dragover", engelle);
    window.addEventListener("drop", engelle);
    return () => {
      window.removeEventListener("dragover", engelle);
      window.removeEventListener("drop", engelle);
    };
  }, []);

  // Yükleme sürerken uygulama içi gezinme (tarayıcının geri tuşu dahil) engellenir: pencere kalkarsa sonuçlar görünmez, döngü
  // durdurulamaz. Sekme kapatılır ya da sayfa yenilenirse tarayıcı sorar.
  const engelle = useCallback(() => calisiyorRef.current, []);
  useBlocker({ shouldBlockFn: engelle, enableBeforeUnload: engelle });

  // Pencere yine de kaldırılırsa (engeli aşan bir gezinme) döngü görünmeden sürmesin. StrictMode'un ikinci bağlaması bayrağı indirir.
  useEffect(() => {
    dur.current = false;
    return () => {
      dur.current = true;
    };
  }, []);

  const ekle = (dosyalar: File[]) => {
    if (!dosyalar.length) return;
    let atlananlar: string[] = [];
    yaz((l) => {
      const r = dosyalariEkle(l, dosyalar);
      atlananlar = r.atlanan;
      return r.satirlar;
    });
    setAtlanan(atlananlar);
  };

  const calistir = async () => {
    if (calisiyorRef.current) return;
    calisiyorRef.current = true;
    setCalisiyor(true);
    setBasladi(true);
    try {
      for (let s = siradaki(liste.current, secim.current); s && !dur.current; s = siradaki(liste.current, secim.current)) {
        const { id } = s;
        yaz((l) => l.map((x) => (x.id === id ? { ...x, durum: "yukleniyor" as const } : x)));
        const g = await gonder(s);
        yaz((l) => l.map((x) => (x.id === id ? { ...x, ...sonucuIsle(g) } : x)));
      }
    } finally {
      calisiyorRef.current = false;
      // Tur bitti: bundan sonra satır düğmesi yalnız kendi satırını gönderir; öteki bekleyenler "Upload"u bekler.
      secim.current = new Set();
      setCalisiyor(false);
      setDurduruluyor(false);
      onBitti();
    }
  };

  /** "Upload": bütün bekleyenler gönderilir. */
  const yukle = () => {
    secim.current = null;
    void calistir();
  };

  /**
   * Satır düğmeleri: satırı yeniden sıraya koyar. Yükleme bir kez başladıysa yalnız o satır gönderilir; süren "Upload" turu onu
   * zaten alır, durdurulmuş ya da bitmiş turun bekleyenleri gönderilmez.
   */
  const yeniden = (id: number, f: (l: SokumSatiri[]) => SokumSatiri[]) => {
    yaz(f);
    if (!basladi) return;
    secim.current = secimeEkle(secim.current, id);
    void calistir();
  };

  const o = topluOzet(satirlar);
  const sayaclar: [string, number, "ok" | "neutral" | "warn" | "bad"][] = [
    [cevir("uploaded"), o.yuklendi, "ok"],
    [cevir("disassembly input attached"), o.girdiBaglandi, "ok"],
    [cevir("not changed"), o.degismedi, "warn"],
    [cevir("riser needed"), o.rnGerekli, "warn"],
    [cevir("needs its PDF"), o.pdfGerekli, "warn"],
    [cevir("failed"), o.hata, "bad"],
  ];
  const suren = [o.yukleniyor ? `${cevir("uploading")} ${o.yukleniyor}` : null, o.bekliyor ? `${cevir("waiting")} ${o.bekliyor}` : null].filter(Boolean).join(" · ");

  return (
    <Modal baslik={cevir("Upload Disassembly reports")} onKapat={onKapat} genis="cok" kilitli={calisiyor}>
      <p className="mb-3 text-sm text-ink-2">{cevir("Select or drop Disassembly report PDFs, with or without their disassembly inputs (R{rn}-disassembly-input-EN.xlsx). The riser is read from the file name; a PDF and an excel of the same riser are paired on one row and sent together. A PDF with its excel fills the record at once and the riser moves to the Field list; a PDF without an excel joins the converter queue and the riser waits for the excel. From here a riser that already has a Disassembly record is not changed; the only exception is an excel for a record that has no disassembly input, sent alone or with its PDF (the PDF on the record is kept). Rows are sent one by one; an error on one row does not stop the others.")}</p>
      <div className="mb-3 rounded-md border border-line bg-surface-2 px-3 py-2 text-sm leading-relaxed text-ink-2">{cevir("A PDF that carries several risers is not uploaded here. Split it into one PDF per riser first.")}</div>
      <div
        className={cn("rounded-md border border-dashed border-line-strong bg-surface-2 px-4 py-6 text-center text-sm text-ink-2", surukleniyor && "border-orange")}
        onDragOver={(e) => {
          e.preventDefault();
          setSurukleniyor(true);
        }}
        onDragLeave={() => setSurukleniyor(false)}
        onDrop={(e) => {
          e.preventDefault();
          setSurukleniyor(false);
          ekle([...e.dataTransfer.files]);
        }}
      >
        {cevir("Drop PDF and excel files here, or")} <Buton kucuk onClick={() => secici.current?.click()}>{cevir("Select files")}</Buton>
        <div className="mt-1 text-xs text-ink-3">{cevir("PDF up to 100 MB each · disassembly input .xlsx")}</div>
        <input
          ref={secici}
          type="file"
          multiple
          accept=".pdf,.xlsx"
          className="hidden"
          onChange={(e) => {
            ekle([...(e.target.files ?? [])]);
            e.target.value = "";
          }}
        />
      </div>
      {atlanan.length > 0 && <Band tip="not" className="mt-3">{cevir("Not a PDF or .xlsx, skipped: {0}", atlanan.join(", "))}</Band>}

      {satirlar.length > 0 && (
        <>
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-ink-2">
            <span>{yerlestir(cevir("{0} of {1} done"), [<b className="tnum">{o.biten}</b>, <b className="tnum">{o.toplam}</b>])}</span>
            {sayaclar.some(([, n]) => n > 0) && (
              <span className="inline-flex flex-wrap gap-1">
                {sayaclar.filter(([, n]) => n > 0).map(([ad, n, ton]) => <Etiket key={ad} tone={ton}>{ad} {n}</Etiket>)}
              </span>
            )}
            {suren && <span className="text-ink-3">{suren}</span>}
          </div>
          <div className="scroll-thin mt-2 overflow-x-auto">
            <table className="w-full min-w-[720px]">
              <thead>
                <tr className="border-b border-line">
                  <th className={tabloBas}>#</th>
                  <th className={tabloBas}>{cevir("Files")}</th>
                  <th className={tabloBas}>RN</th>
                  <th className={tabloBas}>{cevir("Result")}</th>
                </tr>
              </thead>
              <tbody>
                {satirlar.map((s, i) => {
                  const g = satirGorunumu(s);
                  const rn = s.sonuc?.rn ?? etkinRn(s);
                  const taslak = rnTaslak[s.id] ?? s.secilenRn ?? "";
                  return (
                    <tr key={s.id} className="border-b border-line last:border-0">
                      <td className={cn(tabloHucre, "tnum text-ink-3")}>{i + 1}</td>
                      <td className={tabloHucre}>
                        {s.pdf && <Cip>{s.pdf.name}</Cip>}
                        {s.excel && <Cip>{s.excel.name}</Cip>}
                      </td>
                      <td className={tabloHucre}>
                        {g.rnSecimi ? (
                          <div className="flex flex-wrap items-center gap-1.5">
                            <select className={cn(girdi, "max-w-[110px]")} value={taslak} onChange={(e) => setRnTaslak((t) => ({ ...t, [s.id]: e.target.value }))}>
                              <option value="">{cevir("select...")}</option>
                              {rnler.map((r) => <option key={r}>{r}</option>)}
                            </select>
                            <Buton kucuk islem="sokum" disabled={!taslak} onClick={() => yeniden(s.id, (l) => rnSec(l, s.id, taslak))}>{cevir("Retry")}</Buton>
                          </div>
                        ) : rn ? (
                          <b className="font-mono">{rn}</b>
                        ) : (
                          <span className="text-ink-3">—</span>
                        )}
                      </td>
                      <td className={tabloHucre}>
                        {g.etiketler.map((e, j) => (
                          <Fragment key={j}>
                            {j > 0 && " "}
                            <Etiket tone={e.ton}>{e.metin}</Etiket>
                          </Fragment>
                        ))}
                        {g.aciklama && <div className={cn("mt-1 text-xs", METIN_RENGI[g.aciklama.ton])}>{g.aciklama.metin}</div>}
                        {g.notlar.map((n, j) => <div key={j} className={cn("mt-1 text-xs", METIN_RENGI[n.ton])}>{n.metin}</div>)}
                        {g.dugme && (
                          <div className="mt-1 flex flex-wrap gap-1.5">
                            <Buton kucuk islem="sokum" onClick={() => yeniden(s.id, (l) => (g.dugme === "excelsiz" ? excelsizDene(l, s.id) : tekrarDene(l, s.id)))}>{g.dugme === "excelsiz" ? cevir("Retry without excel") : cevir("Retry")}</Buton>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        {/* Upload yükleme sürerken yerinde kalır, kilitlenir; Stop onun yerine çizilmez: Upload'a çift tıkın ikinci tıkı Stop'a düşmesin. */}
        <Buton islem="sokum" tur="birincil" disabled={calisiyor || !o.bekliyor} onClick={yukle}>{cevir("Upload")}</Buton>
        {calisiyor && <Buton disabled={durduruluyor} onClick={() => { secim.current = new Set(); setDurduruluyor(true); }}>{cevir("Stop after this file")}</Buton>}
        <Buton disabled={calisiyor} title={calisiyor ? cevir("Available when the current file finishes") : undefined} onClick={onKapat}>{cevir("Close")}</Buton>
      </div>
      <p className="mt-2 text-xs text-ink-3">{cevir("Closing the window during an upload is blocked; 'Stop after this file' leaves the waiting files unsent. Rows that need you (riser, retry) stay on the list until the window is closed.")}</p>
    </Modal>
  );
}
